import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface GuestGuardProps {
  children: React.ReactNode;
}

/**
 * GuestGuard - Protege rotas públicas (login, register)
 * Redireciona para /dashboard se o usuário já estiver autenticado
 */
export function GuestGuard({ children }: GuestGuardProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Always redirect to /dashboard after login (never to a chat)
      // Clear any saved redirect URL that might point to a chat
      if (typeof window !== 'undefined') {
        const redirectUrl = sessionStorage.getItem('redirect_after_login');
        if (redirectUrl) {
          sessionStorage.removeItem('redirect_after_login');
          // Only redirect if it's NOT a chat URL
          if (!redirectUrl.includes('/dashboard/chat/')) {
            navigate(redirectUrl);
            return;
          }
        }
      }
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[color:var(--brand-500)] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Don't render children if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
